/* eslint-disable react/no-unescaped-entities */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Calendar, Clock, Check, X } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import MobileStickyCTA from "@/components/site/MobileStickyCTA";

const COVER =
  "https://customer-assets-eiarnc6j.emergentagent.net/job_hardscape-pro-5/artifacts/x7m4cm17_IMG_8219.jpeg";
const LVP_IMG =
  "https://customer-assets-eiarnc6j.emergentagent.net/job_hardscape-pro-5/artifacts/paytz0q5_IMG_8223.jpeg";
const LAMINATE_IMG =
  "https://customer-assets-eiarnc6j.emergentagent.net/job_hardscape-pro-5/artifacts/4wknacka_IMG_8732.jpeg";
const TILE_IMG =
  "https://customer-assets.emergentagent.com/job_hardscape-pro-5/artifacts/9z6zytzm_IMG_7768.jpeg";

const CANONICAL =
  "https://mlexteriordesign.com/blog/lvp-vs-laminate-vs-tile-st-louis";
const PUBLISHED = "2026-02-10";
const AUTHOR = "M&L Exterior and Interior Design";

const COMPARISON = [
  { attr: "Waterproof", lvp: true, laminate: false, tile: true },
  { attr: "Best for basements", lvp: true, laminate: false, tile: true },
  { attr: "Best for bathrooms", lvp: true, laminate: false, tile: true },
  { attr: "Best for main-level living", lvp: true, laminate: true, tile: false },
  { attr: "Warm underfoot", lvp: true, laminate: true, tile: false },
  { attr: "Scratch resistant", lvp: true, laminate: true, tile: true },
  { attr: "Kid & pet friendly", lvp: true, laminate: true, tile: true },
  { attr: "Realistic wood look", lvp: true, laminate: true, tile: false },
  { attr: "Lifespan (20+ yrs)", lvp: true, laminate: false, tile: true },
];

export default function BlogLvpVsLaminateVsTile() {
  useEffect(() => {
    const prevTitle = document.title;
    const prevDesc = document
      .querySelector('meta[name="description"]')
      ?.getAttribute("content");
    const prevCanonical = document
      .querySelector('link[rel="canonical"]')
      ?.getAttribute("href");

    document.title =
      "LVP vs Laminate vs Tile · St. Louis Guide | M&L";

    const desc = document.querySelector('meta[name="description"]');
    if (desc) {
      desc.setAttribute(
        "content",
        "A St. Louis flooring contractor's honest guide comparing LVP, laminate, and tile for basements and bathrooms. Waterproofing, cost, durability, and what actually works in Greater St. Louis and St. Charles County homes."
      );
    }

    const canonicalEl = document.querySelector('link[rel="canonical"]');
    if (canonicalEl) canonicalEl.setAttribute("href", CANONICAL);

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "blog-article-schema";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      "@id": `${CANONICAL}#article`,
      headline:
        "LVP vs Laminate vs Tile: The Honest Guide for St. Louis Basements & Bathrooms",
      description:
        "A local flooring contractor's comparison of LVP, laminate, and tile — with clear recommendations for basements, bathrooms, and main-level living in Greater St. Louis and St. Charles County.",
      image: COVER,
      author: {
        "@type": "Organization",
        name: AUTHOR,
        url: "https://mlexteriordesign.com/",
      },
      publisher: {
        "@type": "Organization",
        name: AUTHOR,
        logo: {
          "@type": "ImageObject",
          url: "https://customer-assets.emergentagent.com/job_hardscape-pro-5/artifacts/78rhb5va_IMG_7345.jpeg",
        },
      },
      datePublished: PUBLISHED,
      dateModified: PUBLISHED,
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": CANONICAL,
      },
      about: [
        { "@type": "Thing", name: "Luxury Vinyl Plank" },
        { "@type": "Thing", name: "Laminate Flooring" },
        { "@type": "Thing", name: "Tile Flooring" },
      ],
      articleSection: "Flooring Guide",
    });
    document.head.appendChild(script);

    window.scrollTo(0, 0);

    return () => {
      document.title = prevTitle;
      if (desc && prevDesc) desc.setAttribute("content", prevDesc);
      if (canonicalEl && prevCanonical)
        canonicalEl.setAttribute("href", prevCanonical);
      const s = document.getElementById("blog-article-schema");
      if (s) s.remove();
    };
  }, []);

  return (
    <div
      className="min-h-screen bg-stone-50 text-stone-900"
      data-testid="blog-lvp-laminate-tile-page"
    >
      <Header />

      <main>
        {/* HERO */}
        <article>
          <header className="pt-32 lg:pt-40 pb-10 bg-white border-b border-stone-200">
            <div className="max-w-[880px] mx-auto px-6 lg:px-8">
              <nav
                aria-label="Breadcrumb"
                className="text-xs text-stone-500 mb-6"
              >
                <Link to="/" className="hover:text-orange-600 transition-colors">
                  Home
                </Link>{" "}
                <span className="text-stone-400">/</span>{" "}
                <Link
                  to="/flooring-contractor-st-louis"
                  className="hover:text-orange-600 transition-colors"
                >
                  Flooring Contractor
                </Link>{" "}
                <span className="text-stone-400">/</span>{" "}
                <span className="text-stone-700">
                  LVP vs Laminate vs Tile
                </span>
              </nav>

              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-orange-600 font-semibold mb-6">
                <span className="h-px w-8 bg-orange-600" />
                Flooring Guide · St. Louis
              </div>

              <h1
                data-testid="blog-h1"
                className="font-display font-semibold text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-[-0.025em] text-stone-900"
              >
                LVP vs Laminate vs Tile
                <br />
                <span className="text-stone-500">
                  The honest guide for St. Louis basements &amp; bathrooms.
                </span>
              </h1>

              <div className="mt-8 flex flex-wrap items-center gap-6 text-sm text-stone-500">
                <div className="flex items-center gap-2">
                  <Calendar size={14} />
                  <span>Feb 10, 2026</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock size={14} />
                  <span>6 min read</span>
                </div>
                <div>By {AUTHOR}</div>
              </div>
            </div>
          </header>

          <div className="max-w-[880px] mx-auto px-6 lg:px-8 mt-10">
            <div className="rounded-3xl overflow-hidden bg-stone-200 aspect-[16/9] shadow-[0_20px_60px_-25px_rgba(0,0,0,0.35)]">
              <img
                src={COVER}
                alt="Marble-look tile primary bathroom install in Wentzville, MO by M&L Exterior and Interior Design"
                className="w-full h-full object-cover"
                loading="eager"
              />
            </div>
            <p className="mt-4 text-xs text-stone-500 italic text-center">
              A recent M&amp;L primary-bath tile install — Wentzville, MO.
            </p>
          </div>

          {/* BODY */}
          <div className="max-w-[720px] mx-auto px-6 lg:px-8 py-12 lg:py-16 prose-blog">
            <p className="text-xl leading-[1.7] text-stone-800 font-medium mb-8">
              We install flooring across Greater St. Louis and St. Charles
              County every week — LVP, laminate, and tile — and the same
              question comes up on almost every quote: <em>which one is
              right for my basement, my bathroom, or my main level?</em> This
              is our honest, no-fluff breakdown.
            </p>

            <h2 className="font-display font-semibold text-3xl md:text-4xl text-stone-900 tracking-[-0.02em] leading-tight mt-14 mb-5">
              The short answer
            </h2>
            <ul className="space-y-3 text-[17px] leading-[1.75] text-stone-800 mb-10">
              <li className="flex gap-3">
                <Check
                  size={20}
                  className="text-orange-600 mt-1 shrink-0"
                  strokeWidth={2.5}
                />
                <span>
                  <strong>Basements:</strong> Waterproof LVP wins. Every time.
                </span>
              </li>
              <li className="flex gap-3">
                <Check
                  size={20}
                  className="text-orange-600 mt-1 shrink-0"
                  strokeWidth={2.5}
                />
                <span>
                  <strong>Bathrooms &amp; wet areas:</strong> Tile is the gold
                  standard. LVP is a solid budget alternative.
                </span>
              </li>
              <li className="flex gap-3">
                <Check
                  size={20}
                  className="text-orange-600 mt-1 shrink-0"
                  strokeWidth={2.5}
                />
                <span>
                  <strong>Main-level living rooms &amp; bedrooms:</strong>{" "}
                  Laminate or LVP. Laminate wins on warmth &amp; price; LVP
                  wins on waterproofing.
                </span>
              </li>
            </ul>

            {/* LVP SECTION */}
            <div className="mt-14 rounded-3xl overflow-hidden bg-stone-100 aspect-[16/9] mb-6">
              <img
                src={LVP_IMG}
                alt="Weathered-oak wide-plank LVP flooring in a dining room — M&L install"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>

            <h2 className="font-display font-semibold text-3xl md:text-4xl text-stone-900 tracking-[-0.02em] leading-tight mt-10 mb-5">
              1. LVP (Luxury Vinyl Plank)
            </h2>
            <p className="text-orange-600 font-semibold text-sm uppercase tracking-wider mb-4">
              Waterproof · Durable · The workhorse of St. Louis flooring
            </p>
            <p className="text-[17px] leading-[1.75] text-stone-800 mb-5">
              LVP is our most-requested flooring — and honestly, it's earned
              it. It's 100% waterproof (not just water-resistant), incredibly
              durable, warm underfoot, and looks nearly indistinguishable from
              real hardwood. For most St. Louis and St. Charles County
              homeowners, it's the safe, smart choice.
            </p>
            <p className="text-[17px] leading-[1.75] text-stone-800 mb-5">
              <strong>Where LVP shines:</strong> basements (especially in{" "}
              O'Fallon, Wentzville, and Chesterfield where moisture is a
              constant), kitchens, mudrooms, bathrooms, and any home with kids
              or pets. Because it clicks together over an underlayment, it
              also handles the slight expansion and contraction that comes
              with Missouri seasons.
            </p>
            <p className="text-[17px] leading-[1.75] text-stone-800 mb-5">
              <strong>Cost (installed):</strong> $3–$6 per sq ft labor, plus
              materials. A typical 1,000 sq ft basement runs $3,500–$7,000
              installed depending on materials and prep.
            </p>

            {/* LAMINATE SECTION */}
            <div className="mt-14 rounded-3xl overflow-hidden bg-stone-100 aspect-[16/9] mb-6">
              <img
                src={LAMINATE_IMG}
                alt="Dark hardwood-look laminate flooring in a white kitchen"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>

            <h2 className="font-display font-semibold text-3xl md:text-4xl text-stone-900 tracking-[-0.02em] leading-tight mt-10 mb-5">
              2. Laminate
            </h2>
            <p className="text-orange-600 font-semibold text-sm uppercase tracking-wider mb-4">
              Budget-friendly · Warm · The hardwood look for less
            </p>
            <p className="text-[17px] leading-[1.75] text-stone-800 mb-5">
              Laminate gives you the look and warmth of real hardwood at a
              fraction of the cost, and modern laminate's wear layer actually
              outperforms real wood against scratches, fading, and daily traffic.
              The catch? Traditional laminate is <strong>not
              waterproof</strong>. If water sits on a seam long enough, it
              swells the core.
            </p>
            <p className="text-[17px] leading-[1.75] text-stone-800 mb-5">
              <strong>Where laminate shines:</strong> living rooms, bedrooms,
              hallways, dining rooms, and offices — any dry, controlled space
              on the main level. It's the sweet spot for homeowners who want
              the hardwood look without the hardwood price tag.
            </p>
            <p className="text-[17px] leading-[1.75] text-stone-800 mb-5">
              <strong>Where laminate loses to LVP:</strong> basements,
              bathrooms, kitchens near the sink. If there's any risk of
              standing water, choose LVP instead.
            </p>
            <p className="text-[17px] leading-[1.75] text-stone-800 mb-5">
              <strong>Cost (installed):</strong> $3–$5 per sq ft labor, plus
              materials. Roughly 10–15% cheaper than LVP.
            </p>

            {/* TILE SECTION */}
            <div className="mt-14 rounded-3xl overflow-hidden bg-stone-100 aspect-[16/9] mb-6">
              <img
                src={TILE_IMG}
                alt="Hex-tile primary bathroom install with marble shower — M&L project"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>

            <h2 className="font-display font-semibold text-3xl md:text-4xl text-stone-900 tracking-[-0.02em] leading-tight mt-10 mb-5">
              3. Tile
            </h2>
            <p className="text-orange-600 font-semibold text-sm uppercase tracking-wider mb-4">
              Timeless · Fully waterproof · The premium finish
            </p>
            <p className="text-[17px] leading-[1.75] text-stone-800 mb-5">
              Tile is where craftsmanship really shows. It's fully waterproof,
              lasts decades, and every install is one-of-one — from
              large-format marble-look porcelain in a Chesterfield primary
              bath to hex mosaics in a Central West End powder room.
            </p>
            <p className="text-[17px] leading-[1.75] text-stone-800 mb-5">
              <strong>Where tile shines:</strong> bathrooms (floor and
              shower walls), kitchen backsplashes, entryways, mudrooms, and
              statement floors. If you want it once and you want it forever,
              tile is the answer.
            </p>
            <p className="text-[17px] leading-[1.75] text-stone-800 mb-5">
              <strong>Where tile isn't ideal:</strong> whole-home main levels
              — it's cold underfoot in Missouri winters, and the labor cost
              adds up fast across large open-plan spaces. For living rooms
              and bedrooms, LVP or laminate is almost always the smarter call.
            </p>
            <p className="text-[17px] leading-[1.75] text-stone-800 mb-5">
              <strong>Cost (installed):</strong> $7–$12 per sq ft labor, plus
              materials. Pattern work (herringbone, hex mosaic) adds 15–25%.
            </p>

            {/* COMPARISON TABLE */}
            <h2 className="font-display font-semibold text-3xl md:text-4xl text-stone-900 tracking-[-0.02em] leading-tight mt-16 mb-6">
              Side-by-side comparison
            </h2>
            <div className="overflow-x-auto rounded-2xl border border-stone-200 not-prose mb-10">
              <table
                data-testid="blog-comparison-table"
                className="w-full text-sm"
              >
                <thead className="bg-stone-100">
                  <tr>
                    <th className="text-left px-4 py-3 font-display font-semibold text-stone-900">
                      Feature
                    </th>
                    <th className="px-4 py-3 font-display font-semibold text-stone-900">
                      LVP
                    </th>
                    <th className="px-4 py-3 font-display font-semibold text-stone-900">
                      Laminate
                    </th>
                    <th className="px-4 py-3 font-display font-semibold text-stone-900">
                      Tile
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-200">
                  {COMPARISON.map((row) => (
                    <tr key={row.attr}>
                      <td className="px-4 py-3 text-stone-800 font-medium">
                        {row.attr}
                      </td>
                      {[row.lvp, row.laminate, row.tile].map((v, i) => (
                        <td key={i} className="px-4 py-3 text-center">
                          {v ? (
                            <Check
                              size={18}
                              className="text-emerald-600 inline-block"
                              strokeWidth={2.5}
                            />
                          ) : (
                            <X
                              size={18}
                              className="text-stone-400 inline-block"
                              strokeWidth={2.25}
                            />
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* LOCAL PICKS */}
            <h2 className="font-display font-semibold text-3xl md:text-4xl text-stone-900 tracking-[-0.02em] leading-tight mt-16 mb-6">
              Our local picks by room
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 not-prose mb-10">
              {[
                {
                  room: "Basement",
                  pick: "LVP",
                  copy: "Every St. Louis basement we've walked has some moisture history. Waterproof LVP over a moisture-sealed slab is the only material we recommend.",
                },
                {
                  room: "Primary Bathroom",
                  pick: "Tile",
                  copy: "Large-format marble-look porcelain floors with matching shower walls. If budget is tight, waterproof LVP is a solid alternative.",
                },
                {
                  room: "Kitchen",
                  pick: "LVP or Tile",
                  copy: "LVP for warmth and comfort. Tile if you want it to last 30+ years and don't mind cold winters underfoot.",
                },
                {
                  room: "Living Room / Bedrooms",
                  pick: "LVP or Laminate",
                  copy: "Laminate if the room stays dry and you want the hardwood look for less. LVP if you have kids, pets, or an open plan.",
                },
                {
                  room: "Mudroom / Entryway",
                  pick: "Tile",
                  copy: "Wet boots, snowy shoes, muddy dogs — nothing else handles it long-term. LVP works, but tile is the forever answer.",
                },
                {
                  room: "Full Main Level",
                  pick: "LVP",
                  copy: "For continuity across kitchen, dining, and living spaces, wide-plank waterproof LVP is the safest, most beautiful choice.",
                },
              ].map((r) => (
                <div
                  key={r.room}
                  data-testid={`blog-pick-${r.room.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="rounded-2xl border border-stone-200 bg-white p-6"
                >
                  <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold">
                    {r.room}
                  </div>
                  <div className="mt-1 font-display font-semibold text-2xl text-stone-900 leading-tight">
                    → {r.pick}
                  </div>
                  <p className="mt-3 text-stone-600 text-[15px] leading-relaxed">
                    {r.copy}
                  </p>
                </div>
              ))}
            </div>

            {/* CTA BOX */}
            <div className="mt-16 rounded-3xl bg-stone-900 text-stone-50 p-8 lg:p-10 not-prose">
              <div className="font-display font-semibold text-2xl lg:text-3xl leading-tight">
                Still not sure? Let us walk your space.
              </div>
              <p className="mt-3 text-stone-300 leading-relaxed">
                Free on-site consultation across Greater St. Louis and St.
                Charles County. We'll walk your rooms, check the subfloor, and
                recommend the right material for your budget and lifestyle —
                no pressure, no upsell.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/#contact"
                  data-testid="blog-cta-quote"
                  className="inline-flex items-center gap-2 px-6 py-3.5 bg-orange-500 hover:bg-orange-600 text-stone-50 text-sm font-semibold rounded-full transition-colors group"
                >
                  Get a Free Quote
                  <ArrowRight
                    size={15}
                    className="group-hover:translate-x-0.5 transition-transform"
                  />
                </Link>
                <a
                  href="tel:+16368496681"
                  data-testid="blog-cta-call"
                  className="inline-flex items-center gap-2 px-6 py-3.5 bg-stone-50/10 backdrop-blur-sm border border-stone-50/20 hover:bg-stone-50/20 text-stone-50 text-sm font-semibold rounded-full transition-colors"
                >
                  Call 636-849-6681
                </a>
              </div>
            </div>

            {/* RELATED LINKS */}
            <div className="mt-14 pt-8 border-t border-stone-200 not-prose">
              <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold mb-4">
                Read next
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Link
                  to="/flooring-installation-ofallon-mo"
                  className="text-sm font-semibold text-stone-800 hover:text-orange-600 transition-colors flex items-center gap-2"
                >
                  <ArrowRight size={14} /> Flooring in O'Fallon, MO
                </Link>
                <Link
                  to="/flooring-installation-wentzville-mo"
                  className="text-sm font-semibold text-stone-800 hover:text-orange-600 transition-colors flex items-center gap-2"
                >
                  <ArrowRight size={14} /> Flooring in Wentzville, MO
                </Link>
                <Link
                  to="/flooring-installation-chesterfield-mo"
                  className="text-sm font-semibold text-stone-800 hover:text-orange-600 transition-colors flex items-center gap-2"
                >
                  <ArrowRight size={14} /> Flooring in Chesterfield, MO
                </Link>
              </div>
              <Link
                to="/"
                className="mt-6 inline-flex items-center gap-2 text-sm text-stone-500 hover:text-orange-600 transition-colors"
              >
                <ArrowLeft size={14} /> Back to home
              </Link>
            </div>
          </div>
        </article>
      </main>

      <Footer />
      <MobileStickyCTA />
    </div>
  );
}
